import React, { useState } from "react";
import SectionTitle from "../components/SectionTitle";
import Container from "../components/Container";
import Icon from "../components/Icon";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/SimpleCodeLogo.png";


import translations from "../i18n/servicespage.json";


// Titles that have case studies (both languages)
const CASE_STUDY_TITLES = [
  "Customer Support", "Atención al Cliente",
  "Intelligent Forecasts", "Forecasting Inteligente"
];


// Titles that link to the forecasting product page
const FORECASTING_TITLES = [
  "Intelligent Forecasts", "Pronósticos Inteligentes"
];


function hasCaseStudy(title) {
  return CASE_STUDY_TITLES.includes(title);
}


function hasForecastingPage(title) {
  return FORECASTING_TITLES.includes(title);
}


// 🔹 Orbital System
function OrbitalSystem({ label, services, categoryIndex, selectedKey, onSelect, onNavigate }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const orbitRadius = isMobile ? 80 : 120;
  const containerSize = isMobile ? 260 : 380;
  const center = containerSize / 2;
  const dotSize = isMobile ? 44 : 56;
  const labelDist = orbitRadius + (isMobile ? 46 : 68);


  const selectedIndex = services.findIndex((_, i) => selectedKey === `${categoryIndex}-${i}`);
  const rotationOffset = selectedIndex >= 0
    ? Math.PI - (selectedIndex / services.length) * Math.PI * 2
    : 0;


  return (
    <div className="flex flex-col items-center">
      <h3 className="text-xl font-bold text-sky-700 mb-10">{label}</h3>


      <div
        className="relative"
        style={{ width: containerSize, height: containerSize }}
      >
        <div
          className="absolute rounded-full border border-sky-200/40"
          style={{
            width: orbitRadius * 2,
            height: orbitRadius * 2,
            top: center - orbitRadius,
            left: center - orbitRadius,
          }}
        />


        <div
          className="absolute rounded-full border border-sky-100/30"
          style={{
            width: orbitRadius * 2 + 40,
            height: orbitRadius * 2 + 40,
            top: center - orbitRadius - 20,
            left: center - orbitRadius - 20,
          }}
        />


        <div
          className="absolute rounded-full bg-sky-400/10 blur-2xl"
          style={{
            width: 100,
            height: 100,
            top: center - 50,
            left: center - 50,
          }}
        />


        <motion.img
          src={logo}
          alt="SimplePilot"
          className="absolute z-10"
          style={{
            width: 48,
            height: 48,
            top: center - 24,
            left: center - 24,
            filter: "drop-shadow(0 0 16px rgba(56, 189, 248, 0.3))",
          }}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />


        {services.map((service, i) => {
          const baseAngle = (i / services.length) * Math.PI * 2 - Math.PI / 2;
          const angle = baseAngle + rotationOffset;
          const x = center + Math.cos(angle) * orbitRadius - dotSize / 2;
          const y = center + Math.sin(angle) * orbitRadius - dotSize / 2;
          const key = `${categoryIndex}-${i}`;
          const isSelected = selectedKey === key;


          const lx = center + Math.cos(angle) * labelDist;
          const ly = center + Math.sin(angle) * labelDist;


          return (
            <React.Fragment key={i}>
              <motion.div
                className={`absolute z-20 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-300 ${
                  isSelected
                    ? "bg-sky-600 text-white shadow-lg shadow-sky-600/30"
                    : "bg-white text-sky-600 border-2 border-sky-200 hover:border-sky-400 hover:shadow-md"
                }`}
                style={{ width: dotSize, height: dotSize }}
                animate={{
                  top: y,
                  left: x,
                  scale: isSelected ? 1.15 : 1,
                }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
                onClick={() => onSelect(isSelected ? null : key)}
                whileHover={{ scale: isSelected ? 1.15 : 1.08 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name={service.icon} />
                {hasCaseStudy(service.title) && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 animate-pulse border-2 border-white" />
                )}
              </motion.div>


              <motion.div
                className="absolute text-center pointer-events-none"
                style={{ width: 120 }}
                animate={{
                  top: ly + (Math.sin(angle) > 0.3 ? 16 : Math.sin(angle) < -0.3 ? -28 : -10),
                  left: lx - 60 + (Math.cos(angle) > 0.5 ? 14 : Math.cos(angle) < -0.5 ? -14 : 0),
                  opacity: isSelected ? 0 : 1,
                }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
              >
                <span className={`text-xs font-semibold leading-tight ${
                  isSelected ? "text-sky-600" : "text-slate-600"
                }`}>
                  {service.title}
                </span>
              </motion.div>
            </React.Fragment>
          );
        })}
      </div>


      <div className="w-full mt-4 min-h-[120px]">
        <AnimatePresence mode="wait">
          {services.map((service, i) => {
            const key = `${categoryIndex}-${i}`;
            if (selectedKey !== key) return null;


            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl border border-sky-100 bg-white p-6 shadow-sm"
              >
                <h4 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h4>
                <p className="text-sm text-slate-500 mb-4">{service.desc}</p>


                <div className="space-y-2">
                  {service.bullets.map((area, j) => (
                    <BulletItem
                      key={j}
                      area={area}
                      problemLabel={service.problem}
                      solLabel={service.sol}
                      bulletKey={`${key}-${j}`}
                    />
                  ))}
                </div>


                {hasCaseStudy(service.title) && (
                  <div className="mt-4 pt-4 border-t border-sky-100">
                    <button
                      onClick={() => onNavigate && onNavigate("cases")}
                      className="flex items-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800 transition"
                    >
                      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      {service.problem === "Problem" ? "See the case study →" : "Ver el caso de éxito →"}
                    </button>
                  </div>
                )}


                {hasForecastingPage(service.title) && (
                  <div className="mt-4 pt-4 border-t border-sky-100">
                    <button
                      onClick={() => onNavigate && onNavigate("forecasting")}
                      className="flex items-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800 transition"
                    >
                      <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                      {service.problem === "Problem" ? "See our product →" : "Ver nuestro producto →"}
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>


        {!services.some((_, i) => selectedKey === `${categoryIndex}-${i}`) && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-sm text-slate-400 mt-4"
          >
            Click a service to explore
          </motion.p>
        )}
      </div>
    </div>
  );
}


// 🔹 Expandable bullet with its own open state
function BulletItem({ area, problemLabel, solLabel, bulletKey }) {
  const [isOpen, setIsOpen] = useState(false);


  return (
    <div
      className="rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-sky-50/50 hover:border-sky-200 transition-all cursor-pointer"
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-sky-400" />
          <span className="font-medium text-sm text-slate-800">{area.title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-sky-500"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.div>
      </div>


      <AnimatePresence>
        {isOpen && area.problem && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-1 space-y-3">
              <div className="rounded-lg bg-red-50/60 border border-red-100 p-3">
                <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-1">{problemLabel}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{area.problem}</p>
              </div>


              <div className="flex justify-center">
                <img src={logo} alt="SimplePilot" className="h-5 w-auto opacity-60" />
              </div>


              <div className="rounded-lg bg-sky-50/60 border border-sky-100 p-3">
                <p className="text-xs font-semibold text-sky-500 uppercase tracking-wide mb-1">{solLabel}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{area.solution}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// 🔹 Main Services Page
export default function ServicesPage({ language, onNavigate }) {
  const t = translations[language];
  const [selectedKey, setSelectedKey] = useState(null);


  const mlServices = t.services.slice(0, 3);


  const aiService = t.services[3];
  const aiAsServices = aiService.bullets.map((bullet, i) => ({
    icon: "rocket",
    title: bullet.title,
    desc: aiService.desc,
    problem: aiService.problem,
    sol: aiService.sol,
    bullets: [bullet],
  }));


  return (
    <>
      <section className="py-16 md:py-24 bg-gradient-to-b from-sky-50 via-white to-sky-50 overflow-x-hidden">
        <Container>
          <SectionTitle
            kicker={t.kicker}
            title={t.title}
            subtitle={t.subtitle}
            kickerClassName="text-lg md:text-xl font-semibold text-sky-600"
            titleClassName="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight md:leading-snug lg:leading-snug"
            subtitleClassName="text-sm md:text-base text-slate-600"
            className="max-w-full"
          />


          <div className="mt-16 grid gap-12 md:grid-cols-2">
            <OrbitalSystem
              label="ML & Data Science"
              services={mlServices}
              categoryIndex={0}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onNavigate={onNavigate}
            />
            <OrbitalSystem
              label="AI"
              services={aiAsServices}
              categoryIndex={1}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onNavigate={onNavigate}
            />
          </div>


          {/* ─── Forecasting Product Banner ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="mt-16 rounded-3xl border border-sky-200 bg-gradient-to-r from-sky-600 to-sky-700 p-8 md:p-10 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-sky-400/10 rounded-full blur-2xl pointer-events-none" />


            <div className="relative z-10">
              <span className="inline-block text-xs font-semibold text-sky-200 tracking-wide uppercase mb-3">
                {language === "en" ? "Featured Product" : "Producto Destacado"}
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white">
                RUMBO
              </h2>
              <p className="mt-3 text-base text-sky-100 max-w-xl mx-auto">
                {language === "en"
                  ? "Stop guessing what to send. Our ML-powered system predicts demand and calculates exactly what to ship, where, and when — automatically."
                  : "Dejá de estimar lo que hay que enviar. Nuestro sistema predice la demanda y calcula exactamente qué enviar, a dónde y cuándo — de forma automática."}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => onNavigate("forecasting")}
                  className="rounded-2xl bg-white hover:bg-sky-50 text-sky-700 text-base px-6 py-3 font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {language === "en" ? "Learn More →" : "Conocé más →"}
                </button>
              </div>
            </div>
          </motion.div>


          {/* ─── Tailored Section ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="mt-10 bg-gradient-to-br from-sky-50 to-sky-100/50 rounded-3xl p-10 text-center border border-sky-100"
          >
            <h3 className="text-lg font-semibold text-sky-700">{t.tailored.kicker}</h3>
            <h2 className="mt-2 text-3xl font-bold text-slate-900">{t.tailored.title}</h2>
            {t.tailored.subtitle && (
              <p className="mt-4 text-lg text-slate-700">{t.tailored.subtitle}</p>
            )}
            <p className="mt-6 max-w-xl mx-auto text-slate-600">{t.tailored.body}</p>


            <div className="flex justify-center mt-8">
              <button
                onClick={() => onNavigate("contact")}
                className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white text-lg px-6 py-3 font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-600/30"
              >
                {t.cta?.button || "Contact"}
              </button>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
}



